import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { BASE_API } from "../Config/Config";

function WarehouseDashboard() {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [inward, setInward] = useState({});
    const [outward, setOutward] = useState({});
    const [loadingId, setLoadingId] = useState(null);
    const [error, setError] = useState("");

    const userDetails = JSON.parse(sessionStorage.getItem("userdetails"));

    useEffect(() => {
        fetchWarehouse();
    }, []);

    const fetchWarehouse = async () => {
        try {
            const formData = new FormData();
            formData.append("user_id", userDetails?.id);

            const response = await fetch(
                `${BASE_API}/apis/QAGetManagerWarehouse.php`,
                {
                    method: "POST",
                    body: formData,
                    credentials: "include"
                }
            );

            const result = await response.json();

            if (result.status === "success" && result.data?.length > 0) {
                setData(Array.isArray(result.data) ? result.data : [result.data]);
                setError("");
            } else {
                setData([]);
                setError(result.message || t('noDataFound'));
            }

        } catch (err) {
            console.error("Fetch Error:", err);
            setError("Failed to fetch data");
        }
    };

    const handleSubmit = async (e, item) => {
        e.preventDefault();
        setError("");

        const inwardValue = Number(inward[item.id] || 0);
        const outwardValue = Number(outward[item.id] || 0);

        const totalCapacity = Number(item?.capacity_summary?.[0]?.total_capacity || 0);
        const occupancy = Number(item?.capacity_summary?.[0]?.occupancy || 0);
        const available = totalCapacity - occupancy;

        if (!inwardValue && !outwardValue) {
            setError(t('pleaseEnterValue'));
            return;
        }

        // if (outwardValue > occupancy) {
        //     setError("Outward unit must be less than occupied units");
        //     return;
        // }

        // if (inwardValue > available) {
        //     setError("Inward unit must be less than available capacity");
        //     return;
        // }

        setLoadingId(item.id);

        try {
            const formData = new FormData();
            formData.append("inward", inwardValue);
            formData.append("outward", outwardValue);
            formData.append("id", item.id);

            const response = await fetch(
                `${BASE_API}/apis/QAWarehouseUpdate.php`,
                {
                    method: "POST",
                    body: formData,
                    credentials: "include"
                }
            );

            const result = await response.json();

            if (result?.status === "success") {
                setInward(prev => ({ ...prev, [item.id]: "" }));
                setOutward(prev => ({ ...prev, [item.id]: "" }));
                fetchWarehouse();
                Swal.fire({
                    icon: "success",
                    title: t('success'),
                    text: t('warehouseUpdatedSuccessfully'),
                    timer: 5000
                })
            } else {
                setError(result?.message || t('updateFailed'));
            }

        } catch (err) {
            console.error("Update Error:", err);
            setError(t('somethingWrong'));
        }

        setLoadingId(null);
    };

    if (!data.length)
        return <div className="text-center mt-5">{t('loading')}</div>;

    return (
        <div className="container min-vh-100 p-5">

            {data.map((item, index) => {

                const totalCapacity = Number(item?.capacity_summary?.[0]?.total_capacity || 0);
                const occupancy = Number(item?.capacity_summary?.[0]?.occupancy || 0);
                const available = totalCapacity - occupancy;

                const totalCapa = data.reduce(
                    (acc, item) => acc + (item.capacity_summary?.[0]?.total_capacity || 0),
                    0
                );
                const totalWarehouses = data.length;
                const avgCapacity = totalWarehouses > 0 ? (totalCapa / totalWarehouses).toFixed(0) : 0;

                // Occupancy percent
                const occupancyPercent = totalCapacity > 0
                    ? ((occupancy / totalCapacity) * 100).toFixed(1)
                    : 0;

                // Availability percent (can be negative)
                const availabilityPercent = totalCapacity > 0
                    ? (((available) / totalCapacity) * 100).toFixed(1)
                    : 0;

                // Status color for Occupied card
                const getStatusColor = () => {
                    const percent = Number(occupancyPercent);
                    if (percent > 100) return "danger";       // Over capacity
                    if (percent === 100) return "success";
                    if (percent > 90) return "danger";
                    if (percent > 50) return "warning";
                    if (percent > 30) return "primary";
                    return "success";
                };

                return (
                    <div key={index} className="mb-5">

                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col">
                                <h2 className="fw-bold text-primary">
                                    {item?.location} {t('warehouseDashboard')}
                                </h2>
                            </div>
                        </div>

                        {/* Cards */}
                        <div className="row g-4">
                            <div className="col-md-4">
                                <div className="card shadow border-0 p-4">
                                    <h6>{t('totalCapacity')}</h6>
                                    <h3>{totalCapacity} {t('units')}</h3>
                                    <small className="text-muted">
                                        {t('acrossWarehouses', { count: totalWarehouses, avg: avgCapacity })}
                                    </small>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className={`card shadow border-0 p-4 bg-${getStatusColor()} text-white`}>
                                    <h6>{t('occupied')}</h6>
                                    <h3>{occupancy} {t('units')}</h3>
                                    <small>{occupancyPercent}% {t('used')}</small>
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className={`card shadow border-0 p-4 ${available < 0 ? "bg-danger text-white" : ""}`}>
                                    <h6>{t('available')}</h6>
                                    <h3>{available >= 0 ? available : 0} {t('units')}</h3>
                                    <small>
                                        {available < 0 ? t('exceedStorage') : `${availabilityPercent}% ${t('free')}`}
                                        {available < 0 && ` (${availabilityPercent}%)`}
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Progress Section */}
                        <div className="card shadow border-0 p-4 mt-4">
                            <h5 className="mb-3">{t('storageUsage')}</h5>

                            <div className="progress" style={{ height: "25px" }}>
                                <div
                                    className={`progress-bar bg-${getStatusColor()}`}
                                    role="progressbar"
                                    style={{ width: `${occupancyPercent}%` }}
                                    aria-valuenow={occupancyPercent}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                >
                                    {occupancyPercent}%
                                </div>
                            </div>

                            <p className="text-muted mt-3">
                                {availabilityPercent}% {t('spaceAvailable')}
                            </p>
                        </div>

                        {/* Update Form */}
                        <div className="card shadow border-0 p-4 mt-4">
                            <h5 className="mb-3">{t('updateOccupancy')}</h5>

                            {error && <div className="alert alert-danger">{error}</div>}

                            <form onSubmit={(e) => handleSubmit(e, item)}>
                                <div className="row g-3 align-items-end">

                                    <div className="col-md-3">
                                        <label className="form-label">{t('inward')}</label>
                                        <input
                                            type="number"
                                            min="0"
                                            className="form-control"
                                            value={inward[item.id] || ""}
                                            onChange={(e) =>
                                                setInward(prev => ({
                                                    ...prev,
                                                    [item.id]: e.target.value
                                                }))
                                            }
                                        />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label">{t('outward')}</label>
                                        <input
                                            type="number"
                                            min="0"
                                            className="form-control"
                                            value={outward[item.id] || ""}
                                            onChange={(e) =>
                                                setOutward(prev => ({
                                                    ...prev,
                                                    [item.id]: e.target.value
                                                }))
                                            }
                                        />
                                    </div>

                                    <div className="col-md-2">
                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100"
                                            disabled={loadingId === item.id}
                                        >
                                            {loadingId === item.id ? t('updating') : t('submit')}
                                        </button>
                                    </div>

                                </div>
                            </form>
                        </div>

                    </div>
                );
            })}
        </div>
    );
}

export default WarehouseDashboard;